import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { fixedSlots } from "@/lib/listIdentity";
import { Card3D } from "@/components/Card3D";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { Check, Sparkles } from "lucide-react";

/**
 * SingleAcquireModal
 * Shows ONE card with an "Acquire" button. Use after a single-card purchase
 * or a card-unlock code redemption.
 */
export function SingleAcquireModal({ card, onClose, headline = "CARD ACQUIRED" }) {
  if (!card) return null;
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose?.(); }}>
      <DialogContent className="max-w-2xl border-white/10 bg-[hsl(222_22%_5%)] overflow-hidden p-0">
        <div className="relative min-h-[640px] flex flex-col items-center justify-center p-8">
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div className="absolute inset-0 gridline opacity-20" />
            {fixedSlots(24).map(({ id, index: i }) => (
              <span
                key={id}
                className="sparkle"
                style={{
                  position: "absolute",
                  top: `${Math.random() * 100}%`,
                  left: `${Math.random() * 100}%`,
                  width: 2 + Math.random() * 3,
                  height: 2 + Math.random() * 3,
                  animationDelay: `${Math.random() * 2}s`,
                }}
              />
            ))}
          </div>

          <div className="relative z-10 flex flex-col items-center">
            <div className="font-mono text-[10px] uppercase tracking-[0.4em] text-cyan-300/80 mb-3">
              <Sparkles className="inline h-3 w-3 mr-1" /> {headline}
            </div>

            <motion.div
              initial={{ opacity: 0, scale: 0.7, rotateY: -25 }}
              animate={{ opacity: 1, scale: 1, rotateY: 0 }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            >
              <Card3D card={{ ...card, locked: false }} size="lg" interactive alwaysOwner previewSide="top" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 }}
              className="text-center mt-6"
            >
              <div className={`rarity-${card.rarity} font-display text-3xl text-white tracking-[0.08em]`} style={{ textShadow: `0 0 24px var(--aura)` }}>
                {card.title}
              </div>
              <div className={`rarity-${card.rarity} font-mono text-xs uppercase tracking-[0.3em] mt-1`} style={{ color: "var(--label)" }}>{card.rarity}</div>
              <div className="font-mono text-xs text-white/40 mt-1">{card.serial}</div>
              {card.edition_number != null && (
                <div className="font-mono text-[11px] text-cyan-300 mt-1.5 inline-block px-2 py-0.5 rounded-full bg-cyan-400/10 border border-cyan-400/30" data-testid="acquire-edition-number">
                  EDITION #{card.edition_number}
                </div>
              )}
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
              className="mt-6"
            >
              <Button
                data-testid="single-acquire-button"
                onClick={onClose}
                className="bg-cyan-400 text-black hover:bg-cyan-300 px-6"
              >
                Acquire <Check className="ml-1 h-4 w-4" />
              </Button>
            </motion.div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
